import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, Avatar, TablePagination, useMediaQuery, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor } from '@/types/chip';
import { useNavigate } from 'react-router-dom';
import { Business } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState, useCallback } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from '../shared/BulkActions';
import { CSVExport, type ColumnDef } from '../shared/CSVExport';
import type { ClientRecord } from '../shared/types';

const STATUSES = ['Draft', 'Active', 'Inactive'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
};

const CLIENT_COLUMNS: ColumnDef[] = [
  { field: 'name', header: 'Organisation Name' },
  { field: 'legalEntity', header: 'Company', format: (_: unknown, record: Record<string, unknown>) => (record.legalEntity as Record<string, unknown>)?.companyName as string ?? '' },
  { field: 'legalEntity', header: 'Email', format: (_: unknown, record: Record<string, unknown>) => (record.legalEntity as Record<string, unknown>)?.email as string ?? '' },
  { field: 'legalEntity', header: 'Phone', format: (_: unknown, record: Record<string, unknown>) => (record.legalEntity as Record<string, unknown>)?.phone as string ?? '' },
  { field: 'status', header: 'Status' },
];

export const ClientList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { data: allClients } = useGetList('clients', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const clients = allClients?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allClients?.length ?? 0,
    active: allClients?.filter((c: ClientRecord) => c.status === 'Active').length ?? 0,
    inactive: allClients?.filter((c: ClientRecord) => c.status === 'Inactive').length ?? 0,
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) ids.forEach(id => next.add(id));
      else ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Clients"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
          ]}
        />
        <BulkActions
          selectedIds={Array.from(selectedIds)}
          resourceName="clients"
          selectedCount={selectedIds.size}
          onClearSelection={clearSelection}
          onStatusChange={clearSelection}
          onExportSelected={() => {}}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {clients?.map((client: ClientRecord) => (
            <Paper key={client.id} sx={{ p: 2 }} onClick={() => navigate(`/clients/${client.id}/show`)}>          
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'info.main', fontSize: 14 }}>
                    <Business fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{client.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{client.legalEntity?.email}</Typography>
                  </Box>
                </Box>
                <Chip label={client.status} size="small" color={STATUS_COLORS[client.status]} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Clients"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {allClients && (
              <CSVExport
                data={allClients}
                columns={CLIENT_COLUMNS}
                filename="clients.csv"
                selectedIds={selectedIds.size > 0 ? Array.from(selectedIds) : undefined}
              />
            )}
          </Box>
        }
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Inactive', value: String(stats.inactive), color: 'warning.main' },
        ]}
      />
      <BulkActions
        selectedIds={Array.from(selectedIds)}
        resourceName="clients"
        selectedCount={selectedIds.size}
        onClearSelection={clearSelection}
        onStatusChange={clearSelection}
        onExportSelected={() => {}}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <SelectAllCheckbox items={clients ?? []} selectedIds={selectedIds} onToggleAll={toggleAll} />
              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients?.map((client: ClientRecord) => (
              <TableRow
                key={client.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/clients/${client.id}/show`)}
              >
                <SelectionCheckbox id={client.id} selected={selectedIds.has(client.id)} onToggle={toggleSelect} />
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'info.main', fontSize: 12 }}>
                      <Business fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{client.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{client.legalEntity?.companyName || '—'}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2">{client.legalEntity?.email || '—'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{client.legalEntity?.phone || '—'}</Typography></TableCell>
                <TableCell><Chip label={client.status} size="small" color={STATUS_COLORS[client.status] as ChipColor} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allClients?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!clients || clients.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No clients found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
